import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, ApiError } from "../lib/api";
import { getUser } from "../lib/auth";
import { Pager } from "../components/Pager";
import { SortableTh } from "../components/SortableTh";
import { ConfirmDialog, AlertDialog } from "../components/ConfirmDialog";
import { useAutoPageSize } from "../lib/useAutoPageSize";
import { useSortable } from "../lib/useSortable";
import { usePersistedPage } from "../lib/usePersistedPage";

type SiteUser = {
  id: number;
  email: string;
  display_name: string;
  company: string | null;
  active: boolean;
  status: "pending" | "approved" | "rejected";
  user_type: "normal" | "lp";
  created_at: string;
  submissions: number;
  reports: number;
  pending_lp_apply: number;
};

type LpApplication = {
  id: number;
  user_id: number;
  contact_phone: string | null;
  invested_funds: string | null;
  note: string | null;
  status: "pending" | "approved" | "rejected";
  created_at: string;
  email: string;
  display_name: string;
  company: string | null;
};

const STATUS_LABEL: Record<SiteUser["status"], string> = {
  pending: "待审核",
  approved: "已通过",
  rejected: "已拒绝",
};

const STATUS_COLOR: Record<SiteUser["status"], string> = {
  pending: "#b8860b",
  approved: "#2e8b57",
  rejected: "#b04a3a",
};

export function StudioUsersPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState<SiteUser[] | null>(null);
  const [lpApps, setLpApps] = useState<LpApplication[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = usePersistedPage("kexi_studio_users_page");
  const [delFor, setDelFor] = useState<SiteUser | null>(null);
  const [alertMsg, setAlertMsg] = useState<string | null>(null);
  const isAdmin = getUser()?.role === "admin";
  const { containerRef, pageSize } = useAutoPageSize();

  const reload = () => {
    api.get<SiteUser[]>("/api/admin/studio/users").then(setRows).catch((e) => setError(String(e)));
    api.get<LpApplication[]>("/api/admin/studio/lp-applications").then(setLpApps).catch(() => {});
  };
  useEffect(reload, []);

  const patch = async (id: number, body: { status?: SiteUser["status"]; active?: boolean; user_type?: SiteUser["user_type"] }) => {
    try {
      const updated = await api.patch<SiteUser>(`/api/admin/studio/users/${id}`, body);
      setRows((prev) => prev?.map((r) => (r.id === id ? { ...r, ...updated } : r)) ?? null);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : String(e));
    }
  };

  const doDelete = async () => {
    if (!delFor) return;
    const target = delFor;
    setDelFor(null);
    try {
      await api.delete(`/api/admin/studio/users/${target.id}`);
      setRows((prev) => prev?.filter((r) => r.id !== target.id) ?? null);
    } catch (e) {
      // 有关联数据（提交/报告/LP申请/持仓）→ 后端 409
      if (e instanceof ApiError && e.status === 409) setAlertMsg("该用户有关联数据（智库提交 / 报告 / LP 申请 / 基金持仓），无法删除。可改用「停用」。");
      else setError(e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : String(e));
    }
  };

  const reviewLp = async (id: number, action: "approve" | "reject") => {
    try {
      await api.patch(`/api/admin/studio/lp-applications/${id}`, { action });
      reload(); // 通过后用户 user_type 变 lp，需重拉用户列表
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : String(e));
    }
  };

  const pendingLp = lpApps?.filter((a) => a.status === "pending") ?? [];

  const { sorted, sortKey, sortDir, toggle } = useSortable((rows ?? []) as unknown as Record<string, unknown>[]);
  const pageRows = rows ? (sorted as unknown as SiteUser[]).slice((page - 1) * pageSize, page * pageSize) : null;
  const pendingCount = rows?.filter((r) => r.status === "pending").length ?? 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">用户管理</div>
          <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
            官网注册用户审批与管理；点击用户行进入明细可改资料、重置密码
            {pendingCount > 0 && <span className="ce-dirty" style={{ marginLeft: 10 }}>{pendingCount} 位待审核</span>}
          </div>
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* 待审 LP 申请 */}
      {pendingLp.length > 0 && (
        <div style={{ border: "1px solid var(--line)", marginBottom: 20 }}>
          <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--line)", fontSize: 13, fontWeight: 600 }}>
            LP 申请 · {pendingLp.length} 待审批
          </div>
          {pendingLp.map((a) => (
            <div key={a.id} style={{ padding: "12px 14px", borderBottom: "1px solid var(--line-2, rgba(0,0,0,0.06))", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
              <div style={{ fontSize: 13 }}>
                <strong>{a.display_name}</strong> <span className="muted">{a.email}</span>
                {a.company && <span className="muted"> · {a.company}</span>}
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                  {a.contact_phone && <>电话 {a.contact_phone}　</>}
                  {a.invested_funds && <>投资经历：{a.invested_funds}　</>}
                  {a.note && <>备注：{a.note}</>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button className="btn btn-xs" onClick={() => reviewLp(a.id, "approve")}>通过并设为 LP</button>
                <button className="btn btn-xs" onClick={() => reviewLp(a.id, "reject")}>拒绝</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!rows && !error && <div className="muted">加载中…</div>}
      {rows && rows.length === 0 && <div className="muted">暂无注册用户</div>}

      <div ref={containerRef}>
      {rows && rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              {(() => { const s = { active: sortKey, dir: sortDir, toggle }; return <>
              <SortableTh sortKey="created_at" {...s} style={{ width: 100 }}>注册时间</SortableTh>
              <SortableTh sortKey="display_name" {...s}>用户</SortableTh>
              <SortableTh sortKey="company" {...s} style={{ width: 220 }}>公司</SortableTh>
              <SortableTh sortKey="user_type" {...s} style={{ width: 80 }}>类型</SortableTh>
              <SortableTh sortKey="status" {...s} style={{ width: 100 }}>状态</SortableTh>
              <th style={{ width: 120 }}>操作</th>
              </>; })()}
            </tr>
          </thead>
          <tbody>
            {pageRows!.map((r) => (
              <tr key={r.id} style={{ opacity: r.active ? 1 : 0.5, cursor: "pointer" }}
                onClick={() => nav(`/studio/users/${r.id}`)}>
                <td className="muted">{new Date(r.created_at).toLocaleDateString("zh-CN")}</td>
                <td>
                  {r.display_name}
                  <div className="muted" style={{ fontSize: 11 }}>{r.email}</div>
                </td>
                <td>{r.company || <span className="muted">—</span>}</td>
                <td>
                  {r.user_type === "lp"
                    ? <span style={{ color: "var(--gold, #9a7c3d)", fontWeight: 600 }}>LP</span>
                    : <span className="muted">普通</span>}
                  {r.pending_lp_apply > 0 && <span className="ce-dirty" style={{ marginLeft: 4, fontSize: 10 }}>申请中</span>}
                </td>
                <td>
                  {!r.active
                    ? <span style={{ color: "#999", fontSize: 12 }}>已停用</span>
                    : <span style={{ fontSize: 12, color: STATUS_COLOR[r.status] }}>{STATUS_LABEL[r.status]}</span>}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: 6 }}>
                    {r.status === "pending" && (
                      <>
                        <button className="btn btn-xs" onClick={() => patch(r.id, { status: "approved" })}>通过</button>
                        <button className="btn btn-xs" onClick={() => patch(r.id, { status: "rejected" })}>拒绝</button>
                      </>
                    )}
                    {r.status === "approved" && r.active && (
                      <button className="btn btn-xs" onClick={() => patch(r.id, { active: false })}>停用</button>
                    )}
                    {(r.status === "rejected" || !r.active) && (
                      <button className="btn btn-xs" onClick={() => patch(r.id, { status: "approved", active: true })}>恢复</button>
                    )}
                    {isAdmin && (
                      <button className="btn btn-xs" style={{ color: "#b04a3a", borderColor: "#b04a3a" }} onClick={() => setDelFor(r)}>删除</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {rows && <Pager page={page} pageSize={pageSize} total={rows.length} onChange={setPage} />}
      </div>

      <ConfirmDialog
        open={delFor !== null}
        title="删除用户"
        message={delFor ? <>确认删除用户「{delFor.display_name || delFor.email}」？账号将被永久删除，不可恢复。若该用户有提交/报告/LP 数据则无法删除（请改用停用）。</> : null}
        confirmLabel="删除"
        danger
        onConfirm={doDelete}
        onCancel={() => setDelFor(null)}
      />
      <AlertDialog open={alertMsg !== null} title="无法删除" message={alertMsg} danger onClose={() => setAlertMsg(null)} />
    </div>
  );
}
